import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/client';
import { listProjects } from '../../api/projects';
import type { GrandProjet } from '../../types/estimation';
import { fmt } from '../../utils/format';
import { useTranslation } from 'react-i18next';
import styles from './ReferenceProjectsPage.module.css';

const PAGE_SIZE = 10;

export function ReferenceProjectsPage() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const [projects, setProjects] = useState<GrandProjet[]>([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);

  useEffect(() => {
    loadProjects();
  }, []);

  async function loadProjects() {
    try {
      setLoading(true);
      setError(null);
      const data = await listProjects({ is_custom: false });
      if (data.length === 0) {
        // Auto-seed reference data if none exists
        await autoSeed();
        return;
      }
      setProjects(data);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load reference projects';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  async function autoSeed() {
    try {
      setSeeding(true);
      await api.post('/projects/seed-reference');
      // Reload after seeding
      const data = await listProjects({ is_custom: false });
      setProjects(data);
    } catch {
      // Seed might fail if user isn't admin — just show empty state
      setProjects([]);
    } finally {
      setSeeding(false);
      setLoading(false);
    }
  }

  const totalPages = Math.max(1, Math.ceil(projects.length / PAGE_SIZE));
  const pagedProjects = projects.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  if (loading || seeding) return <div className={styles.loading}>{seeding ? 'Importing reference data...' : t('loading')}</div>;
  if (error) return <div className={styles.loading} style={{ color: 'var(--color-danger, #e74c3c)' }}>{error}</div>;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h2>{t('projects')}</h2>
      </div>

      {projects.length === 0 ? (
        <div className={styles.empty}>{t('noProjects')}</div>
      ) : (
        <>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>{t('name')}</th>
                <th>{t('type')}</th>
                <th>{t('communes')}</th>
                <th className={styles.numericCell}>{t('hectares')}</th>
                <th className={styles.numericCell}>{t('logements')}</th>
                <th className={styles.numericCell}>{t('emplois')}</th>
                <th className={styles.numericCell}>{t('horizon')}</th>
              </tr>
            </thead>
            <tbody>
              {pagedProjects.map((p) => (
                <tr key={p.id} onClick={() => navigate(`/app/reference-projects/${p.id}`)}>
                  <td><span className={styles.projectName}>{p.name}</span></td>
                  <td><span className={styles.typeBadge}>{p.type}</span></td>
                  <td><span className={styles.communesList}>{p.communes.join(', ')}</span></td>
                  <td className={styles.numericCell}>{p.ha_total != null ? `${p.ha_total} ha` : '\u2014'}</td>
                  <td className={styles.numericCell}>{fmt(p.logements)}</td>
                  <td className={styles.numericCell}>{fmt(p.emplois)}</td>
                  <td className={styles.numericCell}>{p.horizon_years ? `${p.horizon_years} ans` : '\u2014'}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {totalPages > 1 && (
            <div className={styles.pagination}>
              <button
                className={styles.pageBtn}
                disabled={page === 0}
                onClick={() => setPage((p) => p - 1)}
              >
                {t('previous')}
              </button>
              <span className={styles.pageInfo}>
                Page {page + 1} of {totalPages}
              </span>
              <button
                className={styles.pageBtn}
                disabled={page >= totalPages - 1}
                onClick={() => setPage((p) => p + 1)}
              >
                {t('next')}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
